import { useEffect, useRef, useState } from "react";
import { useSocket } from "../context/SocketContext";

export default function Comentarios() {
  const { liveUser, socket } = useSocket();

  const [messages, setMessages] = useState([]);

  // Guardado de usuarios
  const [savedIds, setSavedIds] = useState(new Set());
  const [saving, setSaving] = useState({});

  // Auto-scroll control
  const [autoScroll, setAutoScroll] = useState(true);
  const [newCount, setNewCount] = useState(0);

  const listRef = useRef(null);

  // ---- refs para evitar cierres obsoletos y bucles ----
  const autoScrollRef = useRef(true);
  const programmaticScrollRef = useRef(false);

  useEffect(() => {
    autoScrollRef.current = autoScroll;
  }, [autoScroll]);

  // --- Helpers scroll ---
  const isNearBottom = () => {
    const el = listRef.current;
    if (!el) return false;
    const threshold = 80; // px
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  };

  const scrollToBottom = () => {
    const el = listRef.current;
    if (!el) return;

    // Marcamos que este scroll lo hacemos nosotros (para ignorar el onScroll)
    programmaticScrollRef.current = true;

    // Scroll directo dentro del contenedor (no mueve la página)
    el.scrollTop = el.scrollHeight;

    // Liberamos el flag en el siguiente frame (cuando ya se ha asentado el layout)
    requestAnimationFrame(() => {
      programmaticScrollRef.current = false;
    });
  };

  const handleGoToLast = () => {
    scrollToBottom();
    setAutoScroll(true);
    setNewCount(0);
  };

  // 1) Cargar IDs guardados al entrar
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/api/users/ids`);
        const ids = await res.json();
        setSavedIds(new Set(ids));
      } catch (e) {
        console.error("Error cargando ids guardados:", e);
      }
    })();
  }, []);

  // 2) Escuchar chat
  useEffect(() => {
    const s = socket.current;
    if (!s) return;

    const onChat = (msg) => {
      setMessages((prev) => {
        const next = [...prev, msg];
        if (next.length > 300) next.splice(0, next.length - 300);
        return next;
      });

      // Esperar a que React pinte el nuevo mensaje y luego decidir si bajar
      requestAnimationFrame(() => {
        if (autoScrollRef.current) {
          scrollToBottom();
        } else {
          setNewCount((c) => c + 1);
        }
      });
    };

    s.on("chat", onChat);
    return () => s.off("chat", onChat);
  }, [socket]);

  // 3) Detectar scroll del usuario para activar/desactivar autoScroll
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onScroll = () => {
      // Si este scroll lo disparó nuestro autoscroll, no cambiamos estados
      if (programmaticScrollRef.current) return;

      if (isNearBottom()) {
        if (!autoScrollRef.current) {
          setAutoScroll(true);
          setNewCount(0);
        }
      } else {
        if (autoScrollRef.current) setAutoScroll(false);
      }
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // 4) Al entrar, baja al final
  useEffect(() => {
    requestAnimationFrame(() => scrollToBottom());
  }, []);

  // --- Helpers guardado usuarios ---
  const isSaved = (userId) => savedIds.has(String(userId || ""));

  const saveUser = async (msg) => {
    const userId = String(msg.userId || "");
    if (!userId || isSaved(userId)) return;

    setSaving((prev) => ({ ...prev, [userId]: true }));

    try {
      const payload = {
        id: userId,
        secUid: msg.secUid || null,
        displayName: msg.nickname || msg.uniqueId || userId,
        lastKnownUniqueId: msg.uniqueId || null,
        lastKnownNickname: msg.nickname || null,
        profilePictureUrl: msg.profilePictureUrl || null,
      };

      const res = await fetch(`/api/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.status === 409) {
        setSavedIds((prev) => new Set(prev).add(userId));
        return;
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Error guardando usuario");
      }

      setSavedIds((prev) => new Set(prev).add(userId));
    } catch (e) {
      console.error(e);
      alert(String(e.message || e));
    } finally {
      setSaving((prev) => ({ ...prev, [userId]: false }));
    }
  };

  return (
    <div style={{ maxWidth: 950, margin: "30px auto", padding: 20 }}>
      
      <h2 style={{ marginTop: 12 }}>Comentarios - @{liveUser}</h2>

      <div style={{ display: "flex", gap: 12, alignItems: "center", margin: "10px 0" }}>
        <div style={{ opacity: 0.8 }}>
          Auto-scroll: <strong>{autoScroll ? "ON" : "OFF"}</strong>
        </div>

        {!autoScroll && (
          <button onClick={handleGoToLast}>
            Ir al último {newCount > 0 ? `(${newCount} nuevos)` : ""}
          </button>
        )}

        {autoScroll && (
          <button onClick={handleGoToLast} style={{ marginLeft: "auto" }}>
            Ir al último
          </button>
        )}
      </div>

      <div
        ref={listRef}
        style={{
          border: "1px solid #ddd",
          height: 520,
          overflow: "auto",
          borderRadius: 8,
        }}
      >
        <div style={{ padding: 12 }}>
          {messages.map((m, i) => {
            const uid = String(m.userId || "");
            const showSave = uid && !isSaved(uid);

            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: 10,
                  padding: "8px 0",
                  borderBottom: "1px solid #f1f1f1",
                  alignItems: "flex-start",
                }}
              >
                {m.profilePictureUrl && (
                  <img
                    src={m.profilePictureUrl}
                    alt="perfil"
                    style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }}
                  />
                )}

                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <strong>{m.nickname || m.uniqueId}</strong>

                    {showSave && (
                      <button
                        onClick={() => saveUser(m)}
                        disabled={!!saving[uid]}
                        style={{ padding: "4px 10px" }}
                      >
                        {saving[uid] ? "Guardando..." : "Guardar usuario"}
                      </button>
                    )}
                  </div>

                  <div>{m.comment}</div>

                  {uid && (
                    <div style={{ fontSize: 12, opacity: 0.6 }}>
                      ID: {uid}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
